import {
  GridColDef,
  GridColumnGroupingModel,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { UseRosterDataGridParams } from '../types';
import { useRosterTimelineColumns } from './useRosterTimelineColumns/useRosterTimelineColumns';

export const useRosterDataGrid = <
  R extends GridValidRowModel = GridValidRowModel,
  V extends string = never
>({
  rosterDays,
  timelineView,
  fixedColumnsLeft,
  fixedColumnsRight,
  renderTimelineCell,
  timelineColumnWidth,
  timelineRelativeStartDate,
  stretchTimelineColumns
}: UseRosterDataGridParams<R, V>): {
  columns: GridColDef[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const { timeLineColumns, columnGroupingModel } = useRosterTimelineColumns<
    R,
    V
  >({
    rosterDays,
    timelineView,
    renderTimelineCell,
    timelineColumnWidth,
    timelineRelativeStartDate,
    stretchTimelineColumns
  });

  const columns = useMemo(
    () => [
      ...applyFixedColumnDefaults(fixedColumnsLeft),
      ...timeLineColumns,
      ...applyFixedColumnDefaults(fixedColumnsRight)
    ],
    [fixedColumnsLeft, timeLineColumns, fixedColumnsRight]
  );

  return { columns, columnGroupingModel };
};

const applyFixedColumnDefaults = <
  R extends GridValidRowModel = GridValidRowModel
>(
  columns?: GridColDef<R>[]
): GridColDef<R>[] =>
  columns?.map(
    (col) =>
      ({
        // A flex column sizes itself from the free space; a fallback width would only fight it, so it
        // is only applied to columns that do not flex.
        ...(col.flex == null ? { width: 200 } : {}),
        sortable: false,
        disableColumnMenu: true,
        ...col
      }) as GridColDef<R>
  ) || [];
